// High Stakes Poker Betting Interface Styled Components
import styled, { keyframes, css } from 'styled-components';
import { theme } from '../theme';
import { BetLevel } from '../../types/swipe';
import { BET_LEVELS } from '../../constants/swipe';

// Betting Overlay (modal backdrop)
export const BettingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  backdrop-filter: blur(5px);
  z-index: 10020;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0;
    align-items: stretch;
  }
`;

// Close Button
export const BettingCloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  background: var(--bg-secondary);
  border: 2px solid var(--border-light);
  color: var(--text-primary);
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10021;
  
  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-medium);
    transform: rotate(90deg);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: ${theme.spacing.md};
    right: ${theme.spacing.md};
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }
`;

// Animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const celebrate = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  25% {
    transform: scale(1.1) rotate(5deg);
  }
  75% {
    transform: scale(1.1) rotate(-5deg);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Betting Container (inside overlay)
export const BettingContainer = styled.div`
  background: var(--bg-card);
  border-radius: ${theme.borderRadius.xl};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  margin: auto;
  position: relative;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  ${css`animation: ${slideUp} 0.4s ease-out;`}
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 0;
    max-height: none;
    min-height: 100vh;
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    padding-bottom: 120px;
  }
`;

export const BettingContent = styled.div`
  max-width: 100%;
`;

// Header
export const BettingHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
  color: var(--text-primary);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

export const BettingTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 ${theme.spacing.sm} 0;
  text-shadow: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const BettingSubtitle = styled.p`
  font-size: 1.125rem;
  margin: 0;
  color: var(--text-secondary);
  opacity: 1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

// Profile Card Preview
export const ProfileCardPreview = styled.div`
  background: var(--bg-card);
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${theme.shadows.heavy};
  margin-bottom: ${theme.spacing.xl};
`;

export const ProfileImage = styled.div<{ $imageUrl: string }>`
  width: 100%;
  height: 300px;
  background-image: url(${props => props.$imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 250px;
  }
`;

export const ProfileOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  padding: ${theme.spacing.lg};
  color: white;
`;

export const BettingProfileName = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 ${theme.spacing.xs} 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const BettingProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 1rem;
  flex-wrap: wrap;
`;

// Bet Level Selection
export const BetLevelSection = styled.div`
  background: transparent;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl} 0;
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg} 0;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 ${theme.spacing.lg} 0;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const BetLevelTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 ${theme.spacing.lg} 0;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
    margin-bottom: ${theme.spacing.md};
  }
`;

export const BetLevels = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

export const BetLevelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.sm};
  }
`;

// Bet Level Card
export const BetLevelCard = styled.button<{ $isSelected: boolean; $betLevel: BetLevel }>`
  background: var(--bg-card);
  border: 2px solid ${props => props.$isSelected 
    ? theme.colors.primary 
    : 'var(--border-light)'
  };
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.$isSelected ? theme.shadows.medium : theme.shadows.light};
  
  ${props => props.$isSelected && css`
    transform: scale(1.02);
  `}
  
  &:hover {
    background: var(--bg-secondary);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  ${props => props.$isSelected && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(102, 126, 234, 0.1),
        transparent
      );
      ${css`animation: ${shimmer} 2s infinite;`}
    }
  `}
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
  }
`;

export const BetLevelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
`;

export const BetLevelIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
`;

export const BetLevelLabel = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
  margin-left: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.125rem;
  }
`;

export const SelectedCheck = styled.div<{ $isVisible: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: scale(${props => props.$isVisible ? 1 : 0});
  transition: all 0.3s ease;
`;

export const BetLevelDescription = styled.p`
  font-size: 0.9375rem;
  color: var(--text-secondary);
  margin: 0 0 ${theme.spacing.sm} 0;
  line-height: 1.5;
`;

export const BetLevelDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.sm};
`;

export const DetailTag = styled.span`
  background: var(--bg-secondary);
  padding: 4px 12px;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.8125rem;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

// Message Input Section
export const MessageSection = styled.div`
  background: var(--bg-card);
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.light};
  border: 1px solid rgba(0,0,0,0.05);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
  }
`;

export const MessageLabel = styled.label`
  display: block;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: ${theme.spacing.sm};
`;

export const MessageSubtext = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 ${theme.spacing.md} 0;
`;

export const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${theme.spacing.md};
  border: 2px solid var(--border-medium);
  border-radius: ${theme.borderRadius.md};
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: var(--bg-card);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.8;
  }
`;

export const CharacterCount = styled.div<{ $isNearLimit: boolean }>`
  text-align: right;
  font-size: 0.8125rem;
  color: ${props => props.$isNearLimit ? theme.colors.error : 'var(--text-secondary)'};
  margin-top: ${theme.spacing.xs};
  font-weight: ${props => props.$isNearLimit ? 600 : 400};
`;

export const MessageTips = styled.div`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: rgba(255, 255, 255, 0.1);
  border-left: 3px solid rgba(255, 255, 255, 0.5);
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
`;

export const TipTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const TipList = styled.ul`
  margin: 0;
  padding-left: ${theme.spacing.lg};
  
  li {
    margin-bottom: 4px;
  }
`;

// Submit Button
export const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  background: var(--bg-card);
  color: ${theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.heavy};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  &:hover:not(:disabled) {
    transform: translateY(-4px);
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
    ${css`animation: ${celebrate} 0.5s ease;`}
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    font-size: 1.125rem;
  }
`;

// Response Interface (for received requests)
export const ResponseContainer = styled(BettingContainer)``;

export const ResponseCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.heavy};
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
  }
`;

export const TheirBetSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.xl};
  border-bottom: 2px solid var(--border-light);
`;

export const TheirBetLabel = styled.div`
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
`;

export const TheirBetDisplay = styled.div<{ $betLevel: BetLevel }>`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

export const TheirMessage = styled.div`
  background: var(--bg-secondary);
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing.md};
  font-size: 1rem;
  color: var(--text-primary);
  line-height: 1.6;
  font-style: italic;
  border-left: 3px solid ${theme.colors.primary};
`;

// Response Actions
export const ResponseActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

export const ResponseActionButton = styled.button<{ $variant: 'call' | 'raise' | 'fold' | 'accept' | 'counter' | 'decline' }>`
  width: 100%;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  border: none;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  position: relative;
  overflow: hidden;
  
  ${props => {
    if (props.$variant === 'call' || props.$variant === 'accept') {
      return css`
        background: linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(78, 205, 196, 0.3);
        
        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(78, 205, 196, 0.4);
        }
      `;
    } else if (props.$variant === 'raise' || props.$variant === 'counter') {
      return css`
        background: linear-gradient(135deg, #FFE66D 0%, #FFA500 100%);
        color: var(--text-primary);
        box-shadow: 0 4px 12px rgba(255, 165, 0, 0.3);
        
        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 165, 0, 0.4);
        }
      `;
    } else {
      // fold or decline
      return css`
        background: var(--bg-secondary);
        color: var(--text-secondary);
        border: 2px solid var(--border-medium);
        
        &:hover {
          background: rgba(255, 107, 107, 0.1);
          color: #FF6B6B;
          border-color: #FF6B6B;
        }
      `;
    }
  }}
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    font-size: 1rem;
  }
`;

export const ActionIcon = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

export const ActionDescription = styled.p`
  font-size: 0.875rem;
  opacity: 0.8;
  margin: 0;
  font-weight: 400;
`;

// Timeout Warning
export const TimeoutWarning = styled.div<{ $isUrgent: boolean }>`
  background: ${props => props.$isUrgent 
    ? 'rgba(255, 107, 107, 0.1)' 
    : 'rgba(255, 230, 109, 0.1)'
  };
  border: 2px solid ${props => props.$isUrgent ? '#FF6B6B' : '#FFE66D'};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${props => props.$isUrgent ? '#FF6B6B' : '#FFA500'};
  font-weight: 600;
  
  ${props => props.$isUrgent && css`
    animation: ${celebrate} 1s ease-in-out infinite;
  `}
`;

// Additional Components for BettingInterface
export const BetLevelRecommendation = styled.div`
  background: linear-gradient(45deg, #FFD700, #FFA500);
  color: #000;
  padding: 4px 12px;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: ${theme.spacing.xs};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const BetLevelStats = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.lg};
  }
`;

export const StatBadge = styled.div`
  background: var(--bg-secondary);
  backdrop-filter: blur(5px);
  color: var(--text-primary);
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid var(--border-light);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 6px ${theme.spacing.sm};
    font-size: 0.8rem;
  }
`;

export const SubmitSection = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

export const SubmitButtonIcon = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

export const CancelButton = styled.button`
  flex: 1;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-medium);
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--bg-tertiary);
    border-color: var(--border-dark);
    color: var(--text-primary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-size: 1rem;
  }
`;

